<template>
    <div>
        <div v-if="loading" class="center">
            <ds-spinner :size="50" />
        </div>

        <div>
            <div class="progress-bar-threshold-labels">
                <div>
                    <div v-if="!loading" class="progress-bar-lower-limit">
                        0{{ progressBarUnits }}
                    </div>

                    <div v-if="!loading" class="progress-bar-upper-label">
                        {{ progressBarUpperLimit }}{{ progressBarUnits }} (at risk)
                    </div>
                </div>
            </div>

            <ds-progress-bar
                v-if="!loading"
                :progress="progressBarValue"
                :max="progressBarUpperLimit"
                :class="[getProgressBarColor]"
            />
        </div>

        <div v-if="!loading" class="value-container">
            <div class="value">
                {{ numberFormatCompact(progressBarValue) }}{{ progressBarUnits }}
            </div>
        </div>

        <div v-if="!isDataNull" class="donut-chart-container">
            <div id="donut-chart" class="donut-chart" />

            <div class="legend-container">
                <legend-element
                    v-for="entity in dataTypesWithColors"
                    :key="entity.type"
                    :item-type="entity.type"
                    :item-count="entity.number"
                    :item-total="getTotalCount"
                    :label="entity.type"
                    :item-color="entity.color"
                    :hover-content-label="legendHoverLabel"
                    :hover-label-override="entity.hoverLabelOverride"
                />
            </div>
        </div>

        <div v-if="!isDataNull" class="align-right">
            <a v-if="!loading" :href="linkHref">
                {{ linkLabel }} <ds-icon class="learn-more-icon" name="arrow-long-right" />
            </a>
        </div>

        <div v-if="isDataNull">
            <ds-inline-alert leading-icon type="subtle">
                {{ emptyStateMessage }}
            </ds-inline-alert>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';
import LegendElement from './LegendElement.vue';
import { numberFormatCompact } from '@/utils/numbers.util';
import { sortByKey } from '@/utils/email.util';

const COLOR_HEALTHY = 'green';
const COLOR_OVER_UPPER_LIMIT = 'red';
const COLOR_OVER_HEALTHY_LIMIT = 'yellow';
const ITEM_COLORS = [
    '#0040D8',
    '#8BDC00',
    '#36DFFF',
    '#FFC400',
    '#F44366',
    '#9941D0',
];

export default {
    components: { LegendElement },
    props: {
        // Example [{ "type": "unsolicited", "number": 12} ,
        //           { "type": "Spam", "number": 4}]
        dataByType: {
            type: Array,
            default: () => [],
        },
        progressBarUnits: {
            type: String,
            default: '%',
        },
        progressBarValue: {
            type: Number,
            default: null,
        },
        progressBarHealthyLimit: {
            type: Number,
            default: null,
        },
        progressBarUpperLimit: {
            type: Number,
            default: null,
        },
        legendHoverLabel: {
            type: String,
            default: null,
        },
        linkLabel: {
            type: String,
            default: null,
        },
        linkHref: {
            type: String,
            default: null,
        },
        sortedData: {
            type: Boolean,
            default: true,
        },
        emptyStateMessage: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: true,
            colorList: [],
        };
    },
    watch: {
        dataByType(data) {
            const dataForDonut = {};

            this.sortData(data).forEach(
                (entity) => {
                    dataForDonut[entity.type] = entity.number;
                },
            );

            if (dataForDonut) {
                this.renderChart(dataForDonut);
            }
            this.loading = false;
        },
    },
    computed: {
        getProgressBarColor() {
            if (this.progressBarValue <= this.progressBarHealthyLimit) {
                return COLOR_HEALTHY;
            }

            if (this.progressBarValue > this.progressBarUpperLimit) {
                return COLOR_OVER_UPPER_LIMIT;
            }

            return COLOR_OVER_HEALTHY_LIMIT;
        },
        getTotalCount() {
            return this.dataByType.map((entity) => {
                return entity.number;
            }).reduce((accumulator, currentValue) => {
                return accumulator + currentValue;
            });
        },
        dataTypesWithColors() {
            return this.sortData(this.dataByType)?.map((entity, index) => {
                let elementColor = entity.color;

                if (elementColor != null) {
                    this.colorList[index] = entity.color;
                }

                const legendHoverLabelOverride = entity.legendHoverLabel;

                if (elementColor == null) {
                    elementColor = ITEM_COLORS[index % ITEM_COLORS.length];
                }

                return {
                    type: entity.type,
                    number: entity.number,
                    color: elementColor,
                    hoverLabelOverride: legendHoverLabelOverride,
                };
            });
        },
        isDataNull() {
            return !this.loading && Boolean(this.dataByType.length === 0);
        },
    },
    methods: {
        numberFormatCompact,

        renderChart(data) {
            const width = 200;
            const height = 200;
            const margin = 10;
            // The radius of the pieplot is half the width or half the height (smallest one). I subtract a bit of margin.
            const radius = (Math.min(width, height) / 2) - margin;
            // append the svg object to the div called 'my_dataviz'
            const svg = d3.selectAll('#donut-chart')
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .append('g')
                .attr('transform', `translate(${width / 2}, ${height / 2})`);
            // set the color scale
            let color = d3.scaleOrdinal()
                .range(this.colorList);

            if (this.colorList.length === 0) {
                color = d3.scaleOrdinal()
                    .range(ITEM_COLORS);
            }
            // Compute the position of each group on the pie:
            const pie = d3.pie()
                .value((d) => { return d[1]; });
            const dataReady = pie(Object.entries(data));

            // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
            svg
                .selectAll('whatever')
                .data(dataReady)
                .join('path')
                .attr('d', d3.arc()
                    .innerRadius(60)
                    .outerRadius(radius))
                .attr('fill', (d) => { return (color(d.index)); });
        },
        sortData(data) {
            // we need a local copy so that the watcher doesn't go into an infinite loop
            let localData = JSON.parse(JSON.stringify(data));

            if (this.sortedData) {
                // order the data
                localData = sortByKey(localData, 'number');

                // if len data > 6 sum categories greater than 6 into `other` bucket
                const elementsToSlice = 6;

                if (localData.length > elementsToSlice) {
                    const numberInOtherColumn = localData.slice(elementsToSlice - 1)
                        .map((item) => item.number)
                        .reduce((prev, curr) => prev + curr, 0);

                    localData = localData.slice(0, elementsToSlice - 1);
                    localData.push({ type: 'Other', number: numberInOtherColumn });
                }
            }

            return localData;
        },
    },
};
</script>
<style lang="scss" rel="stylesheet/scss" scoped>
.align-right {
    text-align: right;
}
.donut-chart {
    display: flex;
}
.legend-container {
    display: flex;
    flex-direction: column;
}
.learn-more-icon {
    --icon-size: #{px-to-rem(12px)};
}
.progress-bar-lower-limit {
    flex: 50%;
    float: left;
}
.donut-chart-container {
    display: flex;
    justify-content: space-around;
    align-items: center;
}
.progress-bar {
    margin-bottom: 0;
}
.progress-bar-max-text-label {
    flex: 50%;
    text-align: right;
}
.progress-bar-upper-label {
    flex: 50%;
    text-align: right;
}
.value {
    font-size: $font-size-mega;
}
.value-container {
    width: 100%;
}
.green {
    --progress-bar-color: #{$color-green};
    margin-bottom: 0;
}
.red {
    --progress-bar-color: #{$color-red-800};
    margin-bottom: 0;
}
.yellow {
    --progress-bar-color: #{$color-yellow};
    margin-bottom: 0;
}
.center {
    padding-top: 50px;
    text-align: center;
    vertical-align: middle;
}
</style>
